import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { db } from "../config/firebaseConfig";
import { collection, getDocs, query, where, orderBy } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import NavBar from "../components/NavBar";
import LoadPage from "./LoadPage";
import styles from "../styling/searchPage.module.css";
import suggestedStyles from "../styling/suggestedSearches.module.css";
import InitialLoadingScreen from "../components/InitialLoadingScreen";
import threadStyles from "../styling/threadsPage.module.css";

const suggestions = [
  "Investors at Pantheon",
  "Founder of Pantheon",
  "show me game studio founders",
];

const SearchMetadata = ({ metadata }) => {
  const [displayedText, setDisplayedText] = useState("");
  const [isTyping, setIsTyping] = useState(true);

  useEffect(() => {
    if (!metadata?.filters) return;

    // Construct more concise explanation text
    const fullText = `Analyzing ${
      metadata.totalProfiles
    } profiles based on your search.${Object.entries(
      metadata.weights?.weights || {}
    )
      .filter(([_, value]) => value > 0)
      .sort((a, b) => b[1] - a[1])
      .map(([key, value], index, arr) => {
        const percentage = Math.round(value * 100);
        const keyMap = {
          name: "name matches",
          description: "profile descriptions",
          industry: "industry preferences",
          linkedin: "LinkedIn presence",
          externalLinks: "external links",
          skills: "skills",
          work_experience: "work experience",
          education: "education",
        };
        const displayKey = keyMap[key] || key;
        if (index === 0) {
          return ` Prioritizing ${displayKey} (${percentage}%)`;
        } else if (index === arr.length - 1) {
          return ` and ${displayKey} (${percentage}%)`;
        } else {
          return `, ${displayKey} (${percentage}%)`;
        }
      })
      .join("")}. Found ${metadata.filteredCount} relevant matches in ${
      metadata.processingTime
    }ms.`;

    let index = 0;
    setIsTyping(true);

    // Speed up typing effect
    const typingInterval = setInterval(() => {
      if (index < fullText.length) {
        setDisplayedText(fullText.substring(0, index + 1));
        index += 2; // Increment by 2 for faster typing
      } else {
        setIsTyping(false);
        clearInterval(typingInterval);
      }
    }, 10); // Reduced from 20ms to 10ms

    return () => clearInterval(typingInterval);
  }, [metadata]);

  if (!metadata?.filters) return null;

  const { weights = {} } = metadata;

  return (
    <div className={styles["search-metadata"]}>
      <div className={styles["metadata-content"]}>
        <div className={styles["search-explanation"]}>
          {isTyping && (
            <div className={styles["thinking-indicator"]}>
              <span>Thinking...</span>
            </div>
          )}
          <p>{displayedText}</p>
          {!isTyping && weights?.explanation && (
            <p className={styles["search-reasoning"]}>{weights.explanation}</p>
          )}
        </div>
      </div>
    </div>
  );
};

function SearchPage() {
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pinnedThreads, setPinnedThreads] = useState([]);
  const auth = getAuth();
  const navigate = useNavigate();
  const [searchMetadata, setSearchMetadata] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [currentSuggestion, setCurrentSuggestion] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  useEffect(() => {
    const initializeData = async () => {
      await fetchLeaderboardData();
      await fetchPinnedThreads();
      setInitialLoading(false);
    };

    initializeData();
  }, []);

  // Add suggestion rotation effect
  useEffect(() => {
    const interval = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentSuggestion((prev) => (prev + 1) % suggestions.length);
        setIsTransitioning(false);
      }, 300);
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const fetchLeaderboardData = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, "adonis_users"));
      const data = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setLeaderboardData(data);
      setFilteredData(data); // Initially set filtered data to all data
      setLoading(false);
    } catch (error) {
      console.error("Error fetching leaderboard data:", error);
      setError("Failed to load leaderboard");
      setLoading(false);
    }
  };

  const fetchPinnedThreads = async () => {
    try {
      const threadsQuery = query(
        collection(db, "threads"),
        where("pinned", "==", true),
        orderBy("createdAt", "desc")
      );
      const querySnapshot = await getDocs(threadsQuery);
      const threadsData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
      }));
      setPinnedThreads(threadsData);
    } catch (error) {
      console.error("Error fetching pinned threads:", error);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    setHasSearched(true);
    setLoading(true);
    setError(null);
    setSearchMetadata(null);

    try {
      // Use localhost URL when in development
      const apiUrl =
        process.env.NODE_ENV === "development"
          ? "http://localhost:3000/api/search-profiles" // Local development
          : `${process.env.REACT_APP_API_URL}/api/search-profiles`;

      console.log("Making request to:", apiUrl, {
        environment: process.env.NODE_ENV,
        query: searchQuery,
      });

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ query: searchQuery }),
      });

      if (!response.ok) {
        throw new Error(
          `Search failed: ${response.status} ${response.statusText}`
        );
      }

      const data = await response.json();
      console.log("Search response:", data);

      if (data.results) {
        setFilteredData(data.results);
        setSearchMetadata(data.metadata);
      }
    } catch (error) {
      console.error("Search error:", error);
      setError("Failed to search profiles. Please try again.");
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      handleSearch();
    }
  };

  const handleSuggestionClick = (suggestion) => {
    if (!isTransitioning) {
      setSearchQuery(suggestion);
      handleSearch();
    }
  };

  const renderSearchResults = () => {
    if (!hasSearched) return null;

    if (filteredData.length === 0) {
      return (
        <div className="no-results">
          <p>No students found matching your search.</p>
          <p className="no-results-suggestion">
            Try adjusting your search terms.
          </p>
        </div>
      );
    }

    return (
      <div className="search-results-list">
        {filteredData.map((match) => (
          <div key={match.id} className="search-result-item">
            <div className="result-main-info">
              <div className="result-primary">
                <h3>{match.formData?.name || "Anonymous"}</h3>
                <div className="result-tags">
                  <span className="school-tag">
                    NYU {match.formData?.school || "Not specified"}
                  </span>
                  <span className="year-tag">
                    Class of {match.formData?.year || "N/A"}
                  </span>
                  <span className="industry-tag">
                    {match.formData?.industry || "No industry listed"}
                  </span>
                </div>
              </div>
              <p className="user-description">
                {match.formData?.description ||
                  "This student hasn't added a description yet."}
              </p>
            </div>

            <div className="result-right">
              <div className="result-skills">
                {(match.resumeDetails?.skills || [])
                  .slice(0, 3)
                  .map((skill, index) => (
                    <span key={index} className="skill-tag">
                      {skill}
                    </span>
                  ))}
                {match.resumeDetails?.skills?.length > 3 && (
                  <span className="more-skills">
                    +{match.resumeDetails.skills.length - 3} more
                  </span>
                )}
              </div>
              <button
                className="view-profile-button"
                onClick={() => navigate(`/profile/${match.id}`)}
              >
                View Profile
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const handleClearSearch = (filterType, value) => {
    if (!filterType) {
      // Clear all filters and search
      setSearchQuery("");
      setFilteredData([]);
      setSearchMetadata(null);
      setHasSearched(false);
      return;
    }

    // Create a deep copy of current metadata
    const updatedMetadata = {
      ...searchMetadata,
      filters: {
        formData: { ...searchMetadata?.filters?.formData },
        resumeDetails: {
          ...searchMetadata?.filters?.resumeDetails,
          skills: [...(searchMetadata?.filters?.resumeDetails?.skills || [])],
        },
      },
    };

    // Remove the specific filter
    switch (filterType) {
      case "school":
        updatedMetadata.filters.formData.school = null;
        break;
      case "year":
        updatedMetadata.filters.formData.year = null;
        break;
      case "industry":
        updatedMetadata.filters.formData.industry = null;
        break;
      case "skill":
        updatedMetadata.filters.resumeDetails.skills =
          updatedMetadata.filters.resumeDetails.skills.filter(
            (skill) => skill !== value
          );
        break;
    }

    // Update metadata state immediately
    setSearchMetadata(updatedMetadata);

    // Build new search query from updated metadata
    const terms = [];

    if (updatedMetadata.filters.formData.school) {
      terms.push(`school:${updatedMetadata.filters.formData.school}`);
    }
    if (updatedMetadata.filters.formData.year) {
      terms.push(`year:${updatedMetadata.filters.formData.year}`);
    }
    if (updatedMetadata.filters.formData.industry) {
      terms.push(`industry:"${updatedMetadata.filters.formData.industry}"`);
    }
    updatedMetadata.filters.resumeDetails.skills.forEach((skill) => {
      terms.push(`skill:${skill}`);
    });

    const newQuery = terms.join(" ");
    console.log("New query after filter removal:", newQuery);

    setSearchQuery(newQuery);
    if (newQuery) {
      handleSearch(newQuery);
    } else {
      // If query is empty after removal, clear everything
      setFilteredData([]);
      setSearchMetadata(null);
      setHasSearched(false);
    }
  };

  const AppliedFilters = ({ metadata, onClearSearch }) => {
    if (!metadata?.filters) return null;

    const { formData = {}, resumeDetails = {} } = metadata.filters;
    const hasFilters =
      formData.school ||
      formData.year ||
      formData.industry ||
      resumeDetails.skills?.length;

    if (!hasFilters) return null;

    return (
      <div className={styles["applied-filters"]}>
        <div className={styles["filters-header"]}>
          <span>Applied Filters</span>
          <button
            onClick={() => onClearSearch()}
            className={styles["clear-filters"]}
          >
            Clear all
          </button>
        </div>
        <div className={styles["filter-tags"]}>
          {formData.school && (
            <span className={styles["filter-tag"]}>
              School: {formData.school}
              <button
                onClick={() => onClearSearch("school", formData.school)}
                className={styles["remove-tag"]}
              >
                ×
              </button>
            </span>
          )}
          {formData.year && (
            <span className={styles["filter-tag"]}>
              Year: {formData.year}
              <button
                onClick={() => onClearSearch("year", formData.year)}
                className={styles["remove-tag"]}
              >
                ×
              </button>
            </span>
          )}
          {formData.industry && (
            <span className={styles["filter-tag"]}>
              Industry: {formData.industry}
              <button
                onClick={() => onClearSearch("industry", formData.industry)}
                className={styles["remove-tag"]}
              >
                ×
              </button>
            </span>
          )}
          {resumeDetails.skills?.map((skill) => (
            <span key={skill} className={styles["filter-tag"]}>
              Skill: {skill}
              <button
                onClick={() => onClearSearch("skill", skill)}
                className={styles["remove-tag"]}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      </div>
    );
  };

  if (initialLoading) {
    return <InitialLoadingScreen />;
  }

  if (loading && hasSearched) {
    return <LoadPage />;
  }

  if (error) {
    return (
      <div className="leaderboard-container">
        <div className="error">{error}</div>
      </div>
    );
  }

  return (
    <>
      <NavBar />
      <div
        className={`leaderboard-container ${hasSearched ? "has-searched" : ""}`}
      >
        <div className={styles["search-section"]}>
          <div className={styles["search-content"]}>
            <h1>Search Community</h1>
            <div className={styles["search-container"]}>
              <div className={styles["search-input-wrapper"]}>
                <svg
                  className={styles["search-icon"]}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="11" cy="11" r="8" />
                  <path d="M21 21l-4.35-4.35" />
                </svg>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                  placeholder="Search for talent across Pantheon..."
                  className={styles["search-input"]}
                />
              </div>

              {/* Suggested Searches */}
              <div className={suggestedStyles.container}>
                <span className={suggestedStyles.label}>
                  Try searching for:
                </span>
                <div className={suggestedStyles.suggestionWrapper}>
                  {suggestions.map((suggestion, index) => (
                    <button
                      key={`${suggestion}-${index}`}
                      className={`
                        ${suggestedStyles.suggestion}
                        ${
                          index === currentSuggestion
                            ? suggestedStyles.active
                            : ""
                        }
                        ${isTransitioning ? suggestedStyles.exiting : ""}
                      `}
                      onClick={() => handleSuggestionClick(suggestion)}
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>

              <div className={styles["search-actions"]}>
                <button
                  onClick={handleSearch}
                  disabled={loading}
                  className={styles["search-button"]}
                >
                  {loading ? "Searching..." : "Search"}
                </button>
                {/* {!hasSearched && (
                  <Link
                    to="/filter"
                    className={styles["filter-link"]}
                    data-filter="advanced"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                    </svg>
                    Advanced Filters
                  </Link>
                )} */}
              </div>
            </div>
          </div>
          {!hasSearched && (
            <>
              <div className="recommended-profiles">
                <h2>Recommended Profiles</h2>
                <div className="profile-grid">
                  {leaderboardData
                    .filter((profile) => profile.formData?.searchable !== false)
                    .slice(0, window.innerWidth <= 768 ? 2 : 3)
                    .map((profile) => (
                      <div
                        key={profile.id}
                        className="profile-card"
                        onClick={() => navigate(`/profile/${profile.id}`)}
                      >
                        <div className="profile-info">
                          <h3>{profile.formData?.name || "Anonymous"}</h3>
                          <p className="school">
                            {profile.formData?.company || "No description"}
                          </p>
                          <p className="industry">
                            <span className={styles["industry"]}>
                              {profile.formData?.industry ||
                                "No industry listed"}
                            </span>
                          </p>
                          <div className="profile-skills">
                            {(profile.resumeDetails?.skills || [])
                              .slice(0, 3)
                              .map((skill, index) => (
                                <span key={index} className="skill-tag">
                                  {skill}
                                </span>
                              ))}
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>

              {/* Office Hours Booking Card */}
              <div className={styles["office-hours-card"]}>
                <div className={styles["office-hours-content"]}>
                  <h2>Office Hours</h2>
                  <p className={styles["office-hours-description"]}>
                    Book a 30-minute session with our team to discuss your
                    startup, get feedback, and ask questions.
                  </p>
                  <a
                    href="https://calendly.com/nathannl/30-minute-meeting"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles["office-hours-button"]}
                  >
                    Book Office Hours
                  </a>
                </div>
              </div>

              {pinnedThreads.length > 0 && (
                <div className={styles["pinned-threads"]}>
                  <h2>Pinned Threads</h2>
                  <div className={styles["threads-grid"]}>
                    {pinnedThreads.map((thread) => (
                      <div
                        key={thread.id}
                        className={styles["thread-card"]}
                        onClick={() => navigate(`/threads`)}
                      >
                        <div className={styles["thread-header"]}>
                          <div className={styles["author-info"]}>
                            {/* <div className={styles["author-avatar"]}>
                              {thread.authorPhotoURL ? (
                                <img
                                  src={thread.authorPhotoURL}
                                  alt={thread.authorName}
                                />
                              ) : (
                                thread.authorName?.charAt(0)
                              )}
                            </div> */}
                            <div className={styles["thread-info"]}>
                              <h3>{thread.title}</h3>
                              <span className={styles["author-name"]}>
                                {thread.authorName} •{" "}
                                {formatDate(thread.createdAt)}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className={styles["thread-preview"]}>
                          {thread.content
                            .replace(/<br\s*\/?>(\r\n)?/gi, "\n") // Convert <br> to newlines
                            .replace(/<[^>]*>/g, "") // Remove other HTML
                            .replace(/\n/g, " ")}
                        </div>
                        {thread.taggedUsers &&
                          thread.taggedUsers.length > 0 && (
                            <div className={threadStyles["tagged-users"]}>
                              with{" "}
                              {thread.taggedUsers.map((u, idx) => (
                                <Link
                                  key={u.id}
                                  to={`/profile/${u.id}`}
                                  className={threadStyles["tagged-user"]}
                                >
                                  @{u.name}
                                  {idx < thread.taggedUsers.length - 1
                                    ? ", "
                                    : ""}
                                </Link>
                              ))}
                            </div>
                          )}
                        <div className={styles["thread-footer"]}>
                          <div className={styles["interaction-stat"]}>
                            <span>pinned</span>
                          </div>
                          <button className={styles["view-thread-btn"]}>
                            View Thread
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {hasSearched && !loading && !error && (
          <>
            <AppliedFilters
              metadata={searchMetadata}
              onClearSearch={handleClearSearch}
            />
            <SearchMetadata metadata={searchMetadata} />
          </>
        )}

        {loading ? (
          <div
            style={{ textAlign: "center", padding: "2rem", color: "#374151" }}
          >
            Finding the best matches...
          </div>
        ) : error ? (
          <div className="error">{error}</div>
        ) : (
          renderSearchResults()
        )}
      </div>
    </>
  );
}

function formatDate(date) {
  if (!date) return "";

  const now = new Date();
  const threadDate = new Date(date);
  const diffTime = Math.abs(now - threadDate);
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
    if (diffHours === 0) {
      const diffMinutes = Math.floor(diffTime / (1000 * 60));
      return `${diffMinutes}m ago`;
    }
    return `${diffHours}h ago`;
  } else if (diffDays === 1) {
    return "Yesterday";
  } else if (diffDays < 7) {
    return `${diffDays}d ago`;
  } else if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return `${weeks}w ago`;
  } else {
    return threadDate.toLocaleDateString();
  }
}

export default SearchPage;
